import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Typography, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
import AnimatedBear from '../../../components/AnimatedBear';

// ----------------------------------------------------------------------

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F8FAFC',
    borderRadius: 2,
    '& fieldset': { border: '1px solid #E2E8F0' },
    '&:hover fieldset': { border: '1px solid #CBD5E1' },
    '&.Mui-focused fieldset': { border: '2px solid #2563EB' },
  }
};

export default function LoginForm() {
  const { login } = useAuth();
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Username or Email or Phone Number is required'),
    password: Yup.string().required('Password is required'),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: { email: '', password: '' }
  });

  const {
    setError,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const emailValue = watch('email');

  const onSubmit = async (data) => {
    try {
      await login(data.email, data.password);
    } catch (error) {
      if (isMountedRef.current) {
        setError('afterSubmit', error);
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <AnimatedBear isPasswordFocused={isPasswordFocused} textLength={emailValue?.length || 0} />

      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Username, Phone or Email
          </Typography>
          <RHFTextField 
            name="email" 
            placeholder="Username, Phone or Email" 
            sx={inputStyles}
          />
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Password
            </Typography>
            <Link component={RouterLink} variant="body2" to={PATH_AUTH.resetPassword} sx={{ fontWeight: 'bold' }}>
              Forgot Password?
            </Link>
          </Stack>
          <RHFTextField
            name="password"
            placeholder=".........."
            type={showPassword ? 'text' : 'password'}
            onFocus={() => setIsPasswordFocused(true)}
            onBlur={() => setIsPasswordFocused(false)}
            sx={inputStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label="Remember me on this device" />
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{
          py: 1.5,
          borderRadius: 2,
          bgcolor: '#2563EB',
          '&:hover': { bgcolor: '#1D4ED8' },
          boxShadow: 'none',
          fontSize: '1.1rem',
          fontWeight: 'bold',
        }}
      >
        Login
      </LoadingButton>
    </FormProvider>
  );
}
